import React, { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { Z_LAYERS } from '@styles/zLayers.ts';
import { useMinimizedPillIndex } from './panelDockStore.ts';
import { PanelDock } from './usePanelDock.ts';

interface ComponentProps {
  dock: PanelDock;
  icon?: ReactElement;
  label: string;
}

/**
 * What a minimized panel collapses to: a button pinned to the bottom corner of the panel's edge that
 * restores it. Portaled to the body at the panel layer, so it floats over the page like the panel did.
 *
 * Pills on the same edge stack upwards in the order they were minimized (see minimizedPillIndex). On
 * mobile the stack starts above the bottom navigation bar rather than on it. Long labels — a form
 * panel's heading — are truncated.
 */
const MinimizedPanelPill: React.FC<ComponentProps> = ({ dock, icon, label }) => {
  const stackIndex = useMinimizedPillIndex(dock.key);
  if (!dock.minimized) return null;

  return createPortal(
    <div
      className={
        'fixed bottom-[calc(1.5rem+var(--pill-stack)*3.5rem)] max-lg:bottom-[calc(5rem+var(--pill-stack)*3.5rem)] ' + (dock.side === 'left' ? 'left-6' : 'right-6')
      }
      style={{ zIndex: Z_LAYERS.panel, ['--pill-stack' as string]: stackIndex }}
    >
      <RingilButton
        mode={'outlined-rectangle'}
        onClick={dock.restore}
        icon={icon}
        label={<span className="block max-w-[16rem] truncate">{label}</span>}
        dataTestId={`panelDock-${dock.key}-restore`}
      />
    </div>,
    document.body,
  );
};

export default MinimizedPanelPill;
